import React from "react";

import Link from "next/link";

import {
  Tabs as MuiTabs,
  TabProps as MuiTabProps,
  Tab,
  TabsProps,
  css,
  styled,
} from "@mui/material";

import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import { WithClassName } from "@/models/utils.models";
import { ConditionalWrapper } from "@/shared/ConditionalWrapper";
import { skipForwardProp } from "@/utils/skipForwardProp";

import { CloneProps } from "../clone-props/CloneProps";

export type TabProps = {
  label: string;
  selectedOnRoute?: string;
  isDisabled?: boolean;
  tooltipLabel?: string;
  linkRoute?: string;
  className?: string;
};

type PropTypes = TabsProps & {
  tabs: TabProps[];
};

const StyledTabs = styled(MuiTabs)(() => {
  return css`
    .MuiTab-root {
      opacity: 1;

      border-top-left-radius: 6px;
      border-top-right-radius: 6px;

      padding: 20px 8px;
      min-width: 0;
    }
    .MuiTabs-indicator {
      display: none;
    }
  `;
});

type StyledTabProps = {
  isSelected?: boolean;
};

const StyledTab = styled(Tab, {
  shouldForwardProp: skipForwardProp(["isSelected"]),
})<TabProps & StyledTabProps>(({ theme, isSelected }) => {
  return css`
    border-top: 3px solid
      ${isSelected ? theme.palette.pink.main : "transparent"};
    background-color: ${isSelected
      ? theme.palette.white.main + "!important"
      : "transparent"};

    text-transform: capitalize !important;
    font-weight: 600;

    color: ${theme.palette.black.main};

    &.Mui-selected {
      color: ${theme.palette.black.main};
    }
  `;
});

export const Tabs: React.FC<PropTypes & WithClassName> = (tabsProps) => {
  const { tabs, value, ...rest } = tabsProps;

  return (
    <StyledTabs variant="fullWidth" {...rest} value={value}>
      {tabs.map(
        (
          {
            isDisabled,
            tooltipLabel,
            selectedOnRoute: _,
            label,
            className,
            linkRoute,
            ...rest
          },
          i,
        ) => {
          const isSelected = value === i;
          return (
            <CloneProps<MuiTabProps> key={i}>
              {({ label: _, ...clonedProps }) => (
                <Tooltip
                  key={i}
                  title={tooltipLabel}
                  className={`grow max-w-[250px] basis-0 ${className}`}
                >
                  <div className="h-full">
                    <ConditionalWrapper
                      on={!!linkRoute}
                      Wrapper={({ children }) => {
                        return (
                          <Link href={linkRoute as string}>{children}</Link>
                        );
                      }}
                    >
                      <StyledTab
                        key={i}
                        disabled={isDisabled}
                        isSelected={isSelected}
                        label={label}
                        className="text-sm md:text-base w-full h-full"
                        {...clonedProps}
                        {...rest}
                      />
                    </ConditionalWrapper>
                  </div>
                </Tooltip>
              )}
            </CloneProps>
          );
        },
      )}
    </StyledTabs>
  );
};
